import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, AlertCircle, Loader2, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const token = searchParams.get('token');
  const email = searchParams.get('email');

  useEffect(() => {
    if (!token || !email) {
      setError('Lien de réinitialisation invalide.');
    }
  }, [token, email]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/reset-password`, {
        email,
        token,
        new_password: password
      });

      if (response.data.ok) {
        setSuccess(true);
        toast.success('Mot de passe mis à jour !');
        setTimeout(() => navigate('/login'), 3000);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erreur lors de la réinitialisation';
      setError(detail);
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  if (error && !token) {
    return (
      <div 
        className="min-h-screen bg-[#050505] flex items-center justify-center p-6"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(5,5,5,0.95), rgba(5,5,5,0.98))`,
          backgroundSize: 'cover'
        }}
      >
        <div className="glass rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-12 h-12 text-red-500" />
          </div>
          <h2 className="font-heading text-xl text-white mb-2">Lien invalide</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <Link to="/forgot-password">
            <Button className="bg-neon-cyan text-black font-bold rounded-full px-8">
              Demander un nouveau lien
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div 
        className="min-h-screen bg-[#050505] flex items-center justify-center p-6"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(5,5,5,0.95), rgba(5,5,5,0.98))`,
          backgroundSize: 'cover'
        }}
      >
        <div className="glass rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-12 h-12 text-green-500" />
          </div>
          <h2 className="font-heading text-xl text-white mb-2">Mot de passe mis à jour !</h2>
          <p className="text-gray-400 mb-4">Tu peux maintenant te connecter avec ton nouveau mot de passe.</p>
          <p className="text-neon-cyan text-sm">Redirection vers la connexion...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen bg-[#050505] flex items-center justify-center p-6"
      style={{
        backgroundImage: `linear-gradient(to right, rgba(5,5,5,0.95), rgba(5,5,5,0.8)), url('https://images.unsplash.com/photo-1639323251610-d8218455e596?w=1200')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="glass rounded-2xl p-8 max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="/assets/trackvibes-logo.png" 
            alt="TrackVibes" 
            className="h-16 w-auto mx-auto mb-4"
          />
          <h1 className="font-heading text-2xl text-white">Nouveau mot de passe</h1>
          <p className="text-gray-400 text-sm mt-2">
            Choisis un nouveau mot de passe sécurisé
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="bg-red-500/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-gray-300 flex items-center gap-2">
              <Lock className="w-4 h-4 text-neon-cyan" />
              Nouveau mot de passe
            </Label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-neon-cyan h-12 pr-12"
                disabled={loading}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-neon-cyan transition-colors p-1"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <p className="text-xs text-gray-500">Minimum 8 caractères</p>
          </div>

          <div className="space-y-2">
            <Label className="text-gray-300 flex items-center gap-2">
              <Lock className="w-4 h-4 text-neon-cyan" />
              Confirmer le mot de passe
            </Label>
            <div className="relative">
              <Input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-neon-cyan h-12 pr-12"
                disabled={loading}
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-neon-cyan transition-colors p-1"
                tabIndex={-1}
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading || !password || !confirmPassword}
            className="w-full bg-neon-cyan text-black font-bold uppercase tracking-widest hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] rounded-full py-6"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                Mise à jour...
              </span>
            ) : (
              'Mettre à jour le mot de passe'
            )}
          </Button>
        </form>

        <div className="mt-8 text-center">
          <Link to="/login" className="text-gray-400 hover:text-white">
            Retour à la connexion
          </Link>
        </div>
      </div>
    </div>
  );
}
